/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Cascader, Checkbox, Form, Input, Modal, notification, Popover, Row, Select, Space } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import type { ColDef } from 'ag-grid-community'
import SimpleAgGrid from '../../../compnents/simpleTable/SimpleAgGrid'
import { RootDispatch } from '../../../app/store'
import { Materiales, getMaterialsName } from '../../../models/commodity'
import { selectSettings } from '../../../features/user/setting/settingSlice'
import { getCommonCodeCompletionsAsync } from '../../completion/completionSlice'
import { CommonCodeCategory } from '../../../models/commoncode'
import { getMemberPointParticipatoryList, getMemberPointExceptionPage, submitMemberPoint, submitMemberPointException } from '../MembersManagementPageSlice'
import { InvoiceBillingCategories, BillingCategoryName } from '../../../models/billingCategory'
import { useCascaderDropdownRender } from '../../storesearch/query'
import { Checks } from '../../../models/setting'

const normalizePoint = (p: any) => (p == null || p === -1 || p === '-1' ? null : p)

const normalizeMaterialsCategory = (value: any) => {
  if (!value) return []
  if (Array.isArray(value) && value.length === 0) return []
  if (Array.isArray(value) && Array.isArray(value[0])) return value
  if (Array.isArray(value)) return [value]
  return []
}

const getProcuctInfo = (item: any, settings: any) => {
  const materialsCategory = item.materialsCategory
  const packSpec = item.packSpec ? item.packSpec : '-'
  const doseCategory = item.doseCategory ? item.doseCategory : '-'
  const tenantSupplierName = item.tenantSupplierName
    ? item.tenantSupplierName
    : '-'
  const spec = item.spec ? item.spec : '-'
  const model = item.model ? item.model + '/' : ''
  if (settings?.[Checks.MLKG] == 1) {
    if (materialsCategory == '0') {
      return packSpec + '/' + doseCategory + '/' + tenantSupplierName
    } else if (materialsCategory == '1' || materialsCategory == '2') {
      return packSpec + '/' + model + tenantSupplierName
    } else {
      return packSpec + '/' + tenantSupplierName
    }
  } else {
    if (materialsCategory == '0') {
      return packSpec + '/' + doseCategory + '/' + tenantSupplierName
    } else if (materialsCategory == '1' || materialsCategory == '2') {
      return spec + '/' + model + tenantSupplierName
    } else {
      return spec + '/' + tenantSupplierName
    }
  }
}

const getMaterialsCategoryInfo = (materialsCategoryList: any) => {
  const materialsCategory = materialsCategoryList?.filter((v: any) => v?.length == 1)
  const category = materialsCategoryList?.filter((v: any) => v?.length != 1).map((item: any) => item?.[1])
  return {
    materialsCategory: materialsCategory?.length ? materialsCategory?.join(',') : '',
    category: category?.length ? category?.join(',') : '',
    materialsCategoryList: materialsCategoryList,
  }
}

// 积分单元格：自管理编辑态，点击直接进入输入，跨行切换无需二次点击
const PointCell = ({ params, onChange }: { params: any; onChange: (id: any, value: any) => void }) => {
  const [selected, setSelected] = useState(!!params?.node?.selected)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const api = params?.api
    const node = params?.node
    if (!api || !node) return
    const sync = () => setSelected(!!node.selected)
    api.addEventListener('selectionChanged', sync)
    return () => api.removeEventListener('selectionChanged', sync)
  }, [params?.api, params?.node])

  if (!selected) return null

  const d = params.data

  const commit = () => {
    const val = (value ?? '').trim()
    if (val === '') {
      setError('请输入积分')
      return
    }
    if (!/^\d+$/.test(val)) {
      setError('请输入大于等于0的整数')
      return
    }
    d.point = val
    onChange(d.id, val)
    setError(null)
    setEditing(false)
  }

  if (editing) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Input
          autoFocus
          size="small"
          value={value}
          style={error ? { borderColor: '#ff4d4f' } : undefined}
          onChange={(e) => {
            setValue(e.target.value)
            if (error) setError(null)
          }}
          onBlur={commit}
          onPressEnter={commit}
        />
        {error ? (
          <div
            style={{
              color: '#ff4d4f',
              fontSize: 12,
              lineHeight: '13px',
              height: 13,
              marginTop: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {error}
          </div>
        ) : null}
      </div>
    )
  }

  const v = d.point
  return (
    <div
      onClick={() => {
        setValue(v == null || v === '' ? '' : `${v}`)
        setError(null)
        setEditing(true)
      }}
      style={{ cursor: 'pointer', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {v == null || v === '' ? <span style={{ color: '#bfbfbf' }}>点击输入积分</span> : `${v}积分`}
    </div>
  )
}

interface AddItemForPointsProps {
  visible: boolean
  onCancel: () => void
  onSuccess?: () => void
  title?: string
  type?: number
  defaultSelectedRowKeys?: React.Key[]
  defaultSelectedRows?: any[]
  exceptionBillingCategory?: string
}

export const AddItemForPoints = ({
  visible,
  onCancel,
  onSuccess,
  title = '添加商品',
  type = 1,
  defaultSelectedRowKeys,
  defaultSelectedRows,
  exceptionBillingCategory,
}: AddItemForPointsProps) => {
  const dispatch = useDispatch<RootDispatch>()
  const settings = useSelector(selectSettings)
  const [form] = Form.useForm()

  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [pageLoading, setPageLoading] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)

  const [queries, setQueries] = useState<any>({ size: 10, current: 1, state: 1 })
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const [treeData, setTreeData] = useState<any>([])
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)

  const selectedRowsRef = useRef(selectedRows)
  selectedRowsRef.current = selectedRows
  // 积分跨页记忆，避免输入时 setState 导致 Input 失焦
  const pointMapRef = useRef<Record<string, any>>({})

  // 初始化默认选中项
  useEffect(() => {
    if (visible && defaultSelectedRowKeys && defaultSelectedRows) {
      setSelectedRowKeys(defaultSelectedRowKeys)
      setSelectedRows(defaultSelectedRows)
    }
  }, [visible])

  // 构建商品类别级联数据
  useEffect(() => {
    if (visible) {
      const data = Materiales.map((v) => ({
        id: v,
        value: v.toString(),
        label: getMaterialsName(v),
        children: [],
      }))

      dispatch(getCommonCodeCompletionsAsync({ category: CommonCodeCategory.Durg, keyword: '' }))
        .then(unwrapResult)
        .then((res) => {
          const tree = data.map((v) => {
            if (v.id == 0) {
              return { ...v, children: res.records.map((el: any) => ({ value: el.codeName, label: el.codeName })) }
            } else if (v.id == 1) {
              return { ...v, children: [{ value: '材料', label: '材料' }, { value: '器械', label: '器械' }, { value: '消耗品', label: '消耗品' }, { value: '其他', label: '其他' }] }
            } else if (v.id == 2) {
              return { ...v, children: [{ value: '第一类医疗器械', label: '第一类医疗器械' }, { value: '第二类医疗器械', label: '第二类医疗器械' }, { value: '第三类医疗器械', label: '第三类医疗器械' }] }
            } else if (v.id == 4) {
              return { ...v, children: [{ value: '食品', label: '食品' }, { value: '化妆品', label: '化妆品' }, { value: '保健品', label: '保健品' }, { value: '卫生消毒用品', label: '卫生消毒用品' }, { value: '初级农产品', label: '初级农产品' }, { value: '其他产品', label: '其他产品' }] }
            } else {
              return v
            }
          })
          setTreeData(tree)
        })
        .catch(() => {
          // ignore
        })
    }
  }, [visible])

  const handleSearch = (): void => {
    const values = form.getFieldsValue()
    const materialsCategoryList = values?.materialsCategory
    const categoryInfo = getMaterialsCategoryInfo(materialsCategoryList)
    setQueries({ ...queries, ...values, ...categoryInfo, current: 1 })
  }

  const handleQuery = () => {
    handleSearch()
  }

  const dropdownRender = useCascaderDropdownRender(
    treeData,
    () => normalizeMaterialsCategory(form.getFieldValue('materialsCategory')),
    (v) => form.setFieldsValue({ materialsCategory: v }),
    handleQuery,
  )

  // 获取列表（编辑例外时走例外分页接口，并直接传当前开票项目）
  const getPage = () => {
    setPageLoading(true)
    const request = exceptionBillingCategory
      ? getMemberPointExceptionPage({ ...queries, billingCategory: exceptionBillingCategory, catalogType: 0 })
      : getMemberPointParticipatoryList({ ...queries, catalogType: 0, type, materialsCategoryList: undefined })
    dispatch(request)
      .then(unwrapResult)
      .then((res: any) => {
        const defaultMap = new Map<any, any>((defaultSelectedRows || []).map((r: any) => [r.id, r]))
        const records = (res?.records || []).map((r: any) => {
          const def = defaultMap.get(r.id)
          // 已保存行仅用于勾选/保存（id、tenantCatalogId），展示字段以 pageGoods 为准
          const base = def
            ? { ...r, id: def.id ?? r.id, tenantCatalogId: def.tenantCatalogId ?? r.tenantCatalogId }
            : r
          const savedPoint = pointMapRef.current[r.id]
          return {
            ...base,
            point: savedPoint != null && savedPoint !== '' ? savedPoint : normalizePoint(r.point),
          }
        })
        setData(records)
        setTotal(res?.total || 0)
      })
      .finally(() => setPageLoading(false))
  }

  // 弹框打开时（上升沿）重置到第一页
  const prevVisibleRef = useRef(false)
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      setQueries({ size: 10, current: 1, state: 1 })
    }
    prevVisibleRef.current = visible
  }, [visible])

  // 查询条件变化时加载；visible 通过重置 queries 触发，避免打开时重复请求
  useEffect(() => {
    if (visible && !showSelectedOnly) {
      getPage()
    }
  }, [queries, showSelectedOnly])

  const currentPage = queries?.current || 1
  const pageSize = queries?.size || 10

  // 查看已选：合并跨页已选数据 + 积分编辑值，前端分页
  const allSelectedData = selectedRows.filter(Boolean).map((r: any) => {
    const p = pointMapRef.current[r.id]
    return p !== undefined ? { ...r, point: p } : r
  })

  const pagedData = showSelectedOnly
    ? allSelectedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : data

  const displayTotal = showSelectedOnly ? allSelectedData.length : total

  const rowData = showSelectedOnly ? pagedData : data
  const rowDataRef = useRef(rowData)
  rowDataRef.current = rowData

  const handleSelectionChange = (rows: any[], _nodes: any[], ids: any[]) => {
    const currentPageIds = new Set(rowDataRef.current.map((r: any) => r.id))
    const selectedIds = new Set(ids)

    const newKeys = Array.from(
      new Set([...selectedRowKeys.filter((k) => !currentPageIds.has(k)), ...ids])
    )

    const map = new Map(selectedRowsRef.current.filter(Boolean).map((r: any) => [r.id, r]))
    rows.filter(Boolean).forEach((r: any) => map.set(r.id, r))
    const newRows = Array.from(map.values()).filter((r: any) => {
      if (currentPageIds.has(r.id)) return selectedIds.has(r.id)
      return true
    })

    selectedRowsRef.current = newRows
    setSelectedRowKeys(newKeys)
    setSelectedRows(newRows)
  }

  const handleCellClicked = (event: any) => {
    const colField = event?.colDef?.field
    if (colField === 'checkbox') return
    if (colField === 'point') {
      if (!event.node.selected) {
        event.node.setSelected(true)
      }
      return
    }
    event.node.setSelected(!event.node.selected)
  }

  const handleOk = async () => {
    const rows = selectedRowsRef.current.filter(Boolean).map((r: any) => {
      const p = pointMapRef.current[r.id]
      return p !== undefined ? { ...r, point: p } : r
    })

    if (rows.length === 0) {
      notification.warning({ message: '请至少选择一项' })
      return
    }

    setConfirmLoading(true)
    try {
      if (exceptionBillingCategory) {
        // 编辑例外：直接保存例外数据
        const list = rows.map((r: any) => ({
          tenantCatalogId: r.tenantCatalogId ?? r.id,
          billingCategory: exceptionBillingCategory,
          catalogType: 0,
        }))
        try {
          await dispatch(submitMemberPointException({ memberPointExceptions: list })).then(unwrapResult)
        } catch (e) {
          return
        }
        notification.success({ message: '保存成功' })
        onSuccess?.()
        handleCancel()
        return
      }

      // 添加商品：兑换范围需校验积分
      if (type === 0) {
        const hasEmptyPoint = rows.some((r: any) => {
          const p = r.point ?? r.points
          return p == null || p === '' || p === -1 || p === '-1'
        })
        if (hasEmptyPoint) {
          notification.warning({ message: '请填写所有已选项目的积分' })
          return
        }
        const hasInvalidPoint = rows.some((r: any) => {
          const p = String(r.point ?? r.points ?? '')
          return !/^\d+$/.test(p)
        })
        if (hasInvalidPoint) {
          notification.warning({ message: '积分必须为大于等于0的整数' })
          return
        }
      }
      const list = rows.map((r: any) => ({
        isExchange: type,
        name: r.name,
        catalogType: 0,
        tenantCatalogId: r.tenantCatalogId ?? '',
        point: type === 1 ? '' : (r.point ?? r.points ?? ''),
      }))
      try {
        await dispatch(submitMemberPoint(list)).then(unwrapResult)
      } catch (e) {
        return
      }
      notification.success({ message: '保存成功' })
      onSuccess?.()
      handleCancel()
    } finally {
      setConfirmLoading(false)
    }
  }

  const handleCancel = () => {
    setSelectedRowKeys([])
    setSelectedRows([])
    selectedRowsRef.current = []
    pointMapRef.current = {}
    setShowSelectedOnly(false)
    setQueries({ size: 10, current: 1, state: 1 })
    form.resetFields()
    onCancel()
  }

  // 稳定积分列渲染器引用，避免父组件重渲染导致 PointCell 被重建、丢失编辑/校验状态
  const pointCellRenderer = useCallback((params: any) => (
    <PointCell
      params={params}
      onChange={(id: any, value: any) => {
        pointMapRef.current[id] = value
      }}
    />
  ), [])

  const columns: ColDef[] = [
    {
      headerName: '',
      field: 'checkbox',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      pinned: 'left',
      lockPosition: 'left',
      suppressMovable: true,
      resizable: false,
      sortable: false,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: '序号',
      width: 60,
      minWidth: 60,
      maxWidth: 80,
      pinned: 'left',
      lockPosition: 'left',
      suppressMovable: true,
      sortable: false,
      resizable: false,
      cellStyle: { textAlign: 'center' },
      cellRendererFramework: (params: any) => {
        if (params.node.rowPinned) return ''
        return params.node?.rowIndex != null
          ? (currentPage - 1) * pageSize + params.node.rowIndex + 1
          : ''
      },
    },
    {
      headerName: '商品信息',
      flex: 2,
      minWidth: 200,
      sortable: false,
      cellStyle: { textAlign: 'left' },
      cellRendererFramework: (params: any) => {
        const t = params.node.data
        const info = getProcuctInfo(t, settings)
        return (
          <Popover
            content={
              <div>
                <span style={{ fontSize: '16px', fontWeight: 'bolder', paddingRight: '15px' }}>{t.name}</span>
                <span style={{ color: '#999999', fontSize: '14px' }}>{info}</span>
              </div>
            }
            placement="bottom"
          >
            <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              <span style={{ fontSize: '16px', fontWeight: 'bolder', paddingRight: '15px' }}>{t.name}</span>
              <span style={{ color: '#999999', fontSize: '14px' }}>{info}</span>
            </div>
          </Popover>
        )
      },
    },
    {
      headerName: '目录售价',
      field: 'retailPrice',
      width: 140,
      sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRendererFramework: (params: any) =>
        params.value != null ? Number(params.value).toFixed(5) : '',
    },
    {
      headerName: '目录进价',
      field: 'stockPrice',
      width: 140,
      sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRendererFramework: (params: any) =>
        params.value >= 0 ? Number(params.value).toFixed(5) : '-',
    },
    {
      headerName: '库存',
      field: 'totalCount',
      width: 140,
      sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRendererFramework: (params: any) => {
        const totalCount = Number(params.value ?? 0)
        const totalSplitCount = Number(params.data?.totalSplitCount ?? 0)
        const accountUnit = params.data?.accountUnit ?? ''
        const splitUnit = params.data?.splitUnit ?? ''
        // totalSplitCount > 0（含两者都>0）：展示 总量+单位 拆分量+拆分单位
        if (totalSplitCount > 0) {
          return `${totalCount}${accountUnit}${totalSplitCount}${splitUnit}`
        }
        // 只有 totalCount > 0：只展示总量+单位
        if (totalCount > 0) {
          return `${totalCount}${accountUnit}`
        }
        return '-'
      },
    },
    ...(type === 1
      ? []
      : [
          {
            headerName: '积分',
            field: 'point',
            width: 120,
            sortable: false,
            cellStyle: { textAlign: 'center' },
            cellRendererFramework: pointCellRenderer,
          },
        ]),
  ]

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      maskClosable={false}
      width={1200}
      destroyOnClose
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Form.Item name="keyWord" style={{ marginBottom: 0 }}>
            <Input placeholder="商品名称/助记码" allowClear prefix={<SearchOutlined />} style={{ width: 200 }} onChange={handleQuery} />
          </Form.Item>
          <Form.Item name="materialsCategory" style={{ marginBottom: 0 }}>
            <Cascader options={treeData} onChange={handleQuery} multiple maxTagTextLength={12} changeOnSelect placeholder="商品类别" expandTrigger="hover" dropdownRender={dropdownRender} style={{ width: 160 }} />
          </Form.Item>
          {!exceptionBillingCategory && (
            <Form.Item name="billingCategory" style={{ marginBottom: 0 }}>
              <Select placeholder="开票项目" allowClear style={{ width: 160 }} onChange={handleQuery}>
                {InvoiceBillingCategories.map((v) => (
                  <Select.Option value={v} key={v}>{BillingCategoryName(v)}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">查询</Button>
          </Form.Item>
        </Form>
        <Space>
          <span>已选择 {selectedRowKeys.length} 条</span>
          <Checkbox
            checked={showSelectedOnly}
            onChange={(e) => {
              setShowSelectedOnly(e.target.checked)
              setQueries({ size: 10, current: 1, state: 1 })
            }}
          >
            查看已选
          </Checkbox>
        </Space>
      </Row>

      <div style={{ height: 535 }}>
        <SimpleAgGrid
          rowHeight={41}
          isLoading={pageLoading}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          selectedRowKeys={selectedRowKeys}
          getRowId={(r: any) => r.id}
          column={columns}
          page={{
            items: rowData,
            current: currentPage,
            size: pageSize,
            total: displayTotal,
          }}
          onChangePage={(current, pageSize) => {
            setQueries({ ...queries, current, size: pageSize })
          }}
          onSelectionChange={handleSelectionChange}
          onCellClicked={handleCellClicked}
        />
      </div>
    </Modal>
  )
}
